// src/components/NodeDetailModal.tsx
import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {Building2, TrendingUp, Coins, Link, TriangleAlert, Fuel, Pickaxe, Trees, Droplet, Flame} from 'lucide-react';
import {useSimulationStore} from '../../store/useSimulationStore';
import Dot from '../common/StatusDot';
import {
   ComposedChart, Line, XAxis, YAxis, CartesianGrid,
   Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import DetailModal, {KpiItem, ModalSection} from '../common/DetailModal';
import {
   ChartModeToggle,
   NormalizedInfo,
   useChartNormalization,
   ChartMode,
   useChartRange,
   ChartRangeSlider
} from '../common/ChartModeToggle';
import {authHeaders} from '../../store/useAuthStore';
import {apiUrl} from '../../utils/api';
import StatusBadge, {BadgePreset} from "../common/StatusBadge";
import {fmt, fmtValue} from '../../utils/format';
import StatRow from "../common/StatRow";
import ItemList from '../common/ItemList';
import ProgressBar from '../common/ProgressBar';
import {cardInner} from '../../utils/styles';


// ─── Metric config ────────────────────────────────────────────────────────────
const METRICS = {
   population: {label: 'Population', color: '#3b82f6', format: (v: number) => v.toLocaleString()},
   resources: {
      label: 'Resources',
      color: '#10b981',
      format: (v: number) => v.toLocaleString(undefined, {maximumFractionDigits: 0})
   },
   income: {
      label: 'Income',
      color: '#f59e0b',
      format: (v: number) => `+${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`
   },
   emissions: {
      label: 'Emissions',
      color: '#ef4444',
      format: (v: number) => v.toLocaleString(undefined, {maximumFractionDigits: 0})
   },
   stability: {label: 'Stability %', color: '#8b5cf6', format: (v: number) => `${v.toFixed(1)}%`},
   tradeNet: {
      label: 'Trade Net',
      color: '#ec4899',
      format: (v: number) => (v >= 0 ? '+' : '') + v.toLocaleString(undefined, {maximumFractionDigits: 0})
   },
} as const;

type MetricKey = keyof typeof METRICS;

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({active, payload, label, chartData, visibleMetrics, normalized}: any) => {
   if (!active || !payload?.length) return null;
   const raw = chartData.find((d: any) => d.tick === label) ?? {};
   return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-3 shadow-2xl text-xs min-w-[170px]">
         <div className="text-gray-400 mb-2 font-medium flex justify-between">
            <span>Tick {label}</span>
            {normalized && <span className="text-gray-600 italic">normalized view</span>}
         </div>
         {(Object.keys(METRICS) as MetricKey[]).map(key => {
            if (!visibleMetrics[key]) return null;
            const cfg = METRICS[key];
            const rawValue = raw[key];
            if (rawValue === undefined) return null;
            return (
               <div key={key} className="flex justify-between gap-3 mb-1">
                  <span style={{color: cfg.color}} className="font-medium">{cfg.label}</span>
                  <span className="text-white font-mono">{cfg.format(rawValue)}</span>
               </div>
            );
         })}
      </div>
   );
};

// ─── Main component ───────────────────────────────────────────────────────────
const NodeDetailModal: React.FC = () => {
   const selectedNodeId = useSimulationStore(s => s.selectedNodeId);
   const setSelectedNodeId = useSimulationStore(s => s.setSelectedNodeId);
   const nodes = useSimulationStore(s => s.nodes);
   const connections = useSimulationStore(s => s.connections);
   const setSelectedConnectionId = useSimulationStore(s => s.setSelectedConnectionId);
   const resourceNodes = useSimulationStore(s => s.resourceNodes);
   const investments = useSimulationStore(s => s.investments);
   const setSelectedResourceNodeId = useSimulationStore(s => s.setSelectedResourceNodeId);
   
   const [history, setHistory] = useState<any[]>([]);
   const [isLoadingHistory, setIsLoadingHistory] = useState(false);
   const [visibleMetrics, setVisibleMetrics] = useState<Record<MetricKey, boolean>>({
      population: true, resources: true, income: true,
      emissions: false, stability: true, tradeNet: true,
   });
   const [chartMode, setChartMode] = useState<ChartMode>('absolute');
   
   const chartData = useMemo(() => history.map(h => ({
      tick: h.tick,
      population: h.state_data.population,
      resources: Math.round(h.state_data.resources),
      income: Math.round(h.state_data.income || 0),
      emissions: Math.round(h.state_data.emissions),
      stability: parseFloat(((h.state_data.stability || 0) * 100).toFixed(1)),
      tradeNet: Math.round(h.state_data.trade_net || 0),
      status: h.state_data.income_status || 'middle',
   })), [history]);
   
   const {range, setRange, sliced: rangedData} = useChartRange(chartData);
   const metricKeys = useMemo(() => Object.keys(METRICS), []);
   const {normalizedData} = useChartNormalization(rangedData, metricKeys);
   const displayData = chartMode === 'normalized' ? normalizedData : rangedData;
   
   const toggleMetric = (key: MetricKey) =>
      setVisibleMetrics(prev => ({...prev, [key]: !prev[key]}));
   
   const fetchHistory = useCallback(() => {
      if (!selectedNodeId) return;
      setIsLoadingHistory(true);
      fetch(apiUrl(`/api/nodes/${selectedNodeId}/history`), {headers: authHeaders()})
         .then(res => res.json())
         .then(data => {
            setHistory(data);
            setIsLoadingHistory(false);
         })
         .catch(() => setIsLoadingHistory(false));
   }, [selectedNodeId]);
   
   useEffect(() => {
      fetchHistory();
   }, [fetchHistory]);
   
   useEffect(() => {
      if (!selectedNodeId) return;
      const interval = setInterval(fetchHistory, 25000);
      return () => clearInterval(interval);
   }, [fetchHistory, selectedNodeId]);
   
   // ─── Early return guard (AFTER all hooks) ──────────────────────────────────
   const node = nodes.find(n => n.id === selectedNodeId);
   if (!selectedNodeId || !node) return null;
   
   const nodeConnections = connections.filter(
      c => c.node_a_id === node.id || c.node_b_id === node.id
   );
   
   const tradeBalance = nodeConnections.reduce(
      (acc, conn) => {
         const isSeller = conn.node_a_id === node.id
            ? (nodes.find(n => n.id === conn.node_b_id)?.income_status ?? 'poor') > (node.income_status ?? 'poor')
            : (nodes.find(n => n.id === conn.node_a_id)?.income_status ?? 'poor') > (node.income_status ?? 'poor');
         const vol = Number(conn.last_trade_volume) || 0;
         const val = Number(conn.last_trade_value) || 0;
         if (isSeller) {
            acc.resourcesSold += vol;
            acc.creditsGained += val;
         } else {
            acc.resourcesBought += vol;
            acc.creditsSpent += val;
         }
         return acc;
      },
      {resourcesSold: 0, resourcesBought: 0, creditsGained: 0, creditsSpent: 0}
   );
   
   const resPerCap = (node.resources || 0) / Math.max(node.population || 1, 1);
   const capUsage = (node.population || 0) / Math.max(node.capacity_limit || 1, 1);
   const stabPct = (node.stability || 0) * 100;
   const tradeNetVal = tradeBalance.creditsGained - tradeBalance.creditsSpent;
   const tradingConns = nodeConnections.filter(c => (Number(c.last_trade_volume) || 0) > 0);
   
   // ─── KPI cards (toggle chart lines) ──────────────────────────────────────────
   const kpis: KpiItem[] = [
      {
         label: 'Population', value: (node.population || 0).toLocaleString(),
         sub: `cap: ${(node.capacity_limit || 0).toLocaleString()}`,
         color: METRICS.population.color, active: visibleMetrics.population,
         onClick: () => toggleMetric('population'),
      },
      {
         label: 'Resources', value: (node.resources || 0).toLocaleString(undefined, {maximumFractionDigits: 0}),
         sub: `${resPerCap.toFixed(1)}/cap`,
         color: METRICS.resources.color, active: visibleMetrics.resources,
         onClick: () => toggleMetric('resources'),
      },
      {
         label: 'Income', value: `+${(Number(node.income) || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}`,
         sub: `workforce: ${((node.workforce_ratio || 0) * 100).toFixed(0)}%`,
         color: METRICS.income.color, active: visibleMetrics.income,
         onClick: () => toggleMetric('income'),
      },
      {
         label: 'Emissions', value: (node.emissions || 0).toLocaleString(undefined, {maximumFractionDigits: 0}),
         sub: (node.emissions || 0) > 50000 ? 'mortality risk' : 'safe',
         color: METRICS.emissions.color, active: visibleMetrics.emissions,
         onClick: () => toggleMetric('emissions'),
      },
      {
         label: 'Stability', value: `${stabPct.toFixed(1)}%`,
         sub: stabPct > 60 ? 'healthy' : stabPct > 35 ? 'unstable' : 'critical',
         color: METRICS.stability.color, active: visibleMetrics.stability,
         onClick: () => toggleMetric('stability'),
      },
      {
         label: 'Trade Net',
         value: (tradeNetVal >= 0 ? '+' : '') + tradeNetVal.toLocaleString(undefined, {maximumFractionDigits: 0}),
         sub: `${tradeBalance.resourcesSold.toFixed(1)} res sold`,
         color: METRICS.tradeNet.color, active: visibleMetrics.tradeNet,
         onClick: () => toggleMetric('tradeNet'),
      },
   ];
   
   return (
      <DetailModal
         onClose={() => setSelectedNodeId(null)}
         maxWidth="max-w-3xl"
         header={{
            icon: <Building2 className="w-7 h-7 md:w-9 md:h-9" />,
            typeLabel: node.income_status,
            title: node.name,
            subtitle: (
               <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <StatusBadge preset={node.income_status as BadgePreset}>{node.income_status}</StatusBadge>
                  <span className="text-gray-500 text-xs">ID #{node.id}</span>
                  <span className="text-gray-500 text-xs">·</span>
                  <span className="text-gray-500 text-xs">
                 Updated {new Date(node.updated_at).toLocaleTimeString()}
               </span>
                  {tradingConns.length > 0 && (
                     <span className="text-xs text-purple-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse inline-block" />
                        {tradingConns.length} active trade{tradingConns.length > 1 ? 's' : ''}
                  </span>
                  )}
               </div>
            ),
            dotPulse: tradingConns.length > 0,
         }}
         kpis={kpis}
         kpiHint=""
      >
         {/* ── History chart ── */}
         <ModalSection
            title="History"
            icon={<TrendingUp className="w-4 h-4" />}
            titleExtra={
               <div className="flex items-center gap-3">
                  {isLoadingHistory && (
                     <span className="text-xs text-gray-500 animate-pulse">refreshing…</span>
                  )}
                  <span className="text-xs text-gray-500">
                     {chartData.length} snapshots
                     <ChartRangeSlider range={range} onChange={setRange} totalPoints={chartData.length} />
                  </span>
                  <ChartModeToggle mode={chartMode} onModeChange={setChartMode} />
               </div>
            }
         >
            <NormalizedInfo show={chartMode === 'normalized'} />
            
            {chartData.length === 0 ? (
               <div className="h-48 flex items-center justify-center text-gray-600 text-sm">
                  {isLoadingHistory ? 'Loading history…' : 'No snapshot data yet. Snapshots are saved every 5 ticks.'}
               </div>
            ) : (
               <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={displayData} margin={{top: 0, right: 0, bottom: 0, left: 0}}>
                     <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                     <XAxis
                        dataKey="tick"
                        tick={{fill: '#6b7280', fontSize: 11}}
                        axisLine={false}
                        tickLine={false}
                        label={{
                           value: 'tick',
                           position: 'insideBottomRight',
                           offset: -4,
                           fill: '#4b5563',
                           fontSize: 10
                        }}
                     />
                     
                     {chartMode === 'absolute' ? (
                        <>
                           <YAxis
                              yAxisId="left"
                              tick={{fill: '#6b7280', fontSize: 10}}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={v => fmtValue(v)}
                              width={36}
                           />
                           <YAxis
                              yAxisId="right"
                              orientation="right"
                              domain={[0, 100]}
                              tick={{fill: '#8b5cf6', fontSize: 10}}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={v => `${v}%`}
                              width={36}
                           />
                           <ReferenceLine yAxisId="right" y={50} stroke="#8b5cf6" strokeDasharray="4 4" strokeOpacity={0.25} />
                           {(Object.keys(METRICS) as MetricKey[]).filter(k => k !== 'stability').map(key => (
                              <Line key={key} yAxisId="left" type="monotone" dataKey={key}
                                    stroke={METRICS[key].color} strokeWidth={visibleMetrics[key] ? 2 : 0}
                                    dot={false} activeDot={visibleMetrics[key] ? {r: 4, strokeWidth: 0} : false}
                                    hide={!visibleMetrics[key]} isAnimationActive={false} />
                           ))}
                           <Line yAxisId="right" type="monotone" dataKey="stability"
                                 stroke={METRICS.stability.color} strokeWidth={visibleMetrics.stability ? 2 : 0}
                                 strokeDasharray="6 3" dot={false}
                                 activeDot={visibleMetrics.stability ? {r: 4, strokeWidth: 0} : false}
                                 hide={!visibleMetrics.stability} isAnimationActive={false} />
                        </>
                     ) : (
                        <>
                           <YAxis
                              yAxisId="norm"
                              domain={[0, 100]}
                              tick={{fill: '#6b7280', fontSize: 10}}
                              axisLine={false}
                              tickLine={false}
                              tickFormatter={v => `${v}%`}
                              width={36}
                           />
                           {[25, 50, 75].map(y => (
                              <ReferenceLine key={y} yAxisId="norm" y={y}
                                             stroke="#374151" strokeDasharray="3 3" strokeOpacity={0.5} />
                           ))}
                           {(Object.keys(METRICS) as MetricKey[]).map(key => (
                              <Line key={key} yAxisId="norm" type="monotone" dataKey={key}
                                    stroke={METRICS[key].color} strokeWidth={visibleMetrics[key] ? 2 : 0}
                                    strokeDasharray={key === 'stability' ? '6 3' : 'none'}
                                    dot={false} activeDot={visibleMetrics[key] ? {r: 4, strokeWidth: 0} : false}
                                    hide={!visibleMetrics[key]} isAnimationActive={false} />
                           ))}
                        </>
                     )}
                     
                     <Tooltip
                        content={
                           <CustomTooltip
                              chartData={chartData}
                              visibleMetrics={visibleMetrics}
                              normalized={chartMode === 'normalized'}
                           />
                        }
                        cursor={{stroke: '#374151', strokeWidth: 1}}
                     />
                     <Legend
                        wrapperStyle={{paddingTop: 12}}
                        formatter={(value) => {
                           const key = value as MetricKey;
                           return (
                              <span style={{
                                 color: visibleMetrics[key] ? METRICS[key].color : '#4b5563',
                                 fontSize: 11,
                              }}>
                          {METRICS[key].label}
                                 {chartMode === 'normalized' && visibleMetrics[key] && (
                                    <span style={{color: '#4b5563', fontSize: 9}}> (scaled)</span>
                                 )}
                        </span>
                           );
                        }}
                     />
                  </ComposedChart>
               </ResponsiveContainer>
            )}
         </ModalSection>
         
         {/* ── Housing & capacity ── */}
         <ModalSection title="Housing & Capacity" icon={<Building2 className="w-4 h-4" />}>
            <div className="grid grid-cols-3 gap-3 mb-3">
               
               {/* ── Housing Units ── */}
               <div className={`${cardInner} p-3 text-center relative group`}>
                  <div className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-1">
                     Housing Units
                     <span className="text-gray-600 hover:text-gray-300 cursor-help transition-colors">ⓘ</span>
                  </div>
                  <div className="text-xl font-bold text-blue-400">
                     {(node.housing_units || 0).toLocaleString()}
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 z-50
                      invisible group-hover:visible opacity-0 group-hover:opacity-100
                      transition-all duration-150 pointer-events-none">
                     <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-left shadow-xl space-y-2">
                        <p className="text-sm font-semibold text-gray-100">Housing Units</p>
                        <p className="text-xs text-gray-400 leading-relaxed">Built automatically each tick when the node is approaching capacity and can afford it.</p>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Build conditions</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Occupancy threshold</span>
                              <span className="text-xs text-white font-mono">&gt; 85%</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Min res/capita</span>
                              <span className="text-xs text-white font-mono">≥ 60</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Max units/tick</span>
                              <span className="text-xs text-white font-mono">50</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Budget used</span>
                              <span className="text-xs text-white font-mono">10% of resources</span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Cost per unit by income</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-red-400 font-mono">poor</span>
                              <span className="text-xs text-white font-mono">20 resources</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-yellow-400 font-mono">middle</span>
                              <span className="text-xs text-white font-mono">50 resources</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-blue-400 font-mono">rich</span>
                              <span className="text-xs text-white font-mono">120 resources</span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">This node right now</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Income status</span>
                              <span className="text-xs text-white font-mono">{node.income_status}</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Cost per unit</span>
                              <span className="text-xs text-white font-mono">
                {node.income_status === 'poor' ? 20 : node.income_status === 'rich' ? 120 : 50} resources
              </span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Build budget</span>
                              <span className="text-xs text-white font-mono">
                {Math.floor((node.resources || 0) * 0.10).toLocaleString()} resources
              </span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Affordable units</span>
                              <span className="text-xs text-white font-mono">
                {Math.min(Math.floor(((node.resources || 0) * 0.10) / (node.income_status === 'poor' ? 20 : node.income_status === 'rich' ? 120 : 50)), 50).toLocaleString()} units
              </span>
                           </div>
                           <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                              <span className="text-xs text-gray-400">Will build this tick?</span>
                              <span className={`text-xs font-mono font-semibold ${capUsage >= 0.85 && (node.resources || 0) / Math.max(node.population || 1, 1) >= 60 ? 'text-green-400' : 'text-gray-500'}`}>
                {capUsage >= 0.85 && (node.resources || 0) / Math.max(node.population || 1, 1) >= 60 ? 'Yes ✓' : 'No'}
              </span>
                           </div>
                        </div>
                     </div>
                     <div className="w-2 h-2 bg-gray-900 border-r border-b border-gray-700 rotate-45 mx-auto -mt-1" />
                  </div>
               </div>
               
               {/* ── Capacity Limit ── */}
               <div className={`${cardInner} p-3 text-center relative group`}>
                  <div className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-1">
                     Capacity Limit
                     <span className="text-gray-600 hover:text-gray-300 cursor-help transition-colors">ⓘ</span>
                  </div>
                  <div className="text-xl font-bold text-white">
                     {(node.capacity_limit || 0).toLocaleString()}
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 z-50
                      invisible group-hover:visible opacity-0 group-hover:opacity-100
                      transition-all duration-150 pointer-events-none">
                     <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-left shadow-xl space-y-2">
                        <p className="text-sm font-semibold text-gray-100">Capacity Limit</p>
                        <p className="text-xs text-gray-400">Formula: <span className="text-white font-mono">housing_units × occupancy_rate</span></p>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Occupancy rate by income</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-red-400 font-mono">poor</span>
                              <span className="text-xs text-white font-mono">× 1.3 <span className="text-gray-500">(cramped)</span></span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-yellow-400 font-mono">middle</span>
                              <span className="text-xs text-white font-mono">× 1.0 <span className="text-gray-500">(standard)</span></span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-blue-400 font-mono">rich</span>
                              <span className="text-xs text-white font-mono">× 0.8 <span className="text-gray-500">(spacious)</span></span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">This node</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Housing units</span>
                              <span className="text-xs text-white font-mono">{(node.housing_units || 0).toLocaleString()}</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Income status</span>
                              <span className="text-xs text-white font-mono">{node.income_status}</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Occupancy rate</span>
                              <span className="text-xs text-white font-mono">× {node.income_status === 'poor' ? '1.3' : node.income_status === 'rich' ? '0.8' : '1.0'}</span>
                           </div>
                           <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                              <span className="text-xs text-gray-300 font-semibold">= Capacity</span>
                              <span className="text-xs text-white font-mono font-semibold">{(node.capacity_limit || 0).toLocaleString()}</span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Auto-build triggers when</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Occupancy</span>
                              <span className="text-xs text-white font-mono">&gt; 85%</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Res/capita</span>
                              <span className="text-xs text-white font-mono">≥ 60</span>
                           </div>
                        </div>
                     </div>
                     <div className="w-2 h-2 bg-gray-900 border-r border-b border-gray-700 rotate-45 mx-auto -mt-1" />
                  </div>
               </div>
               
               {/* ── Occupancy ── */}
               <div className={`${cardInner} p-3 text-center relative group`}>
                  <div className="text-xs text-gray-400 mb-1 flex items-center justify-center gap-1">
                     Occupancy
                     <span className="text-gray-600 hover:text-gray-300 cursor-help transition-colors">ⓘ</span>
                  </div>
                  <div className={`text-xl font-bold ${
                     capUsage > 0.95 ? 'text-red-400' :
                        capUsage > 0.80 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                     {(capUsage * 100).toFixed(1)}%
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 z-50
                      invisible group-hover:visible opacity-0 group-hover:opacity-100
                      transition-all duration-150 pointer-events-none">
                     <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-left shadow-xl space-y-2">
                        <p className="text-sm font-semibold text-gray-100">Occupancy</p>
                        <p className="text-xs text-gray-400">Formula: <span className="text-white font-mono">population / capacity_limit</span></p>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">This node</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Population</span>
                              <span className="text-xs text-white font-mono">{(node.population || 0).toLocaleString()}</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Capacity</span>
                              <span className="text-xs text-white font-mono">{(node.capacity_limit || 0).toLocaleString()}</span>
                           </div>
                           <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                              <span className="text-xs text-gray-400">Headroom</span>
                              <span className={`text-xs font-mono font-semibold ${capUsage > 1 ? 'text-red-400' : 'text-green-400'}`}>
                {capUsage > 1
                   ? `+${((node.population || 0) - (node.capacity_limit || 0)).toLocaleString()} over`
                   : `${((node.capacity_limit || 0) - (node.population || 0)).toLocaleString()} free`}
              </span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Overcrowding mortality</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Grace period</span>
                              <span className="text-xs text-white font-mono">5 ticks</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Base death rate</span>
                              <span className="text-xs text-white font-mono">0.8% / tick</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Ramps up over</span>
                              <span className="text-xs text-white font-mono">15 ticks</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Max death rate</span>
                              <span className="text-xs text-red-400 font-mono">5% / tick</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-gray-400">Cost per death</span>
                              <span className="text-xs text-white font-mono">10 resources</span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2 space-y-1">
                           <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Thresholds</p>
                           <div className="flex justify-between">
                              <span className="text-xs text-green-400 font-mono">● safe</span>
                              <span className="text-xs text-gray-400 font-mono">&lt; 80%</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-yellow-400 font-mono">● warning</span>
                              <span className="text-xs text-gray-400 font-mono">auto-build &gt; 80%</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-orange-400 font-mono">● critical</span>
                              <span className="text-xs text-gray-400 font-mono">overcrowding &gt; 95%</span>
                           </div>
                           <div className="flex justify-between">
                              <span className="text-xs text-red-400 font-mono">● overflow</span>
                              <span className="text-xs text-gray-400 font-mono">deaths after grace &gt; 100%</span>
                           </div>
                        </div>
                        
                        <div className="border-t border-gray-700 pt-2">
                           <div className="flex justify-between items-center">
                              <span className="text-xs text-gray-400">Current status</span>
                              <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${
                                 capUsage > 1    ? 'bg-red-900/50 text-red-400' :
                                    capUsage > 0.95 ? 'bg-orange-900/50 text-orange-400' :
                                       capUsage > 0.80 ? 'bg-yellow-900/50 text-yellow-400' :
                                          'bg-green-900/50 text-green-400'
                              }`}>
                {capUsage > 1
                   ? <><Dot color="#f87171" /> overflow</>
                   : capUsage > 0.95 ? <><Dot color="#fb923c" /> critical</>
                   : capUsage > 0.80 ? <><Dot color="#facc15" /> warning</>
                   : <><Dot color="#4ade80" /> safe</>}
              </span>
                           </div>
                        </div>
                     </div>
                     <div className="w-2 h-2 bg-gray-900 border-r border-b border-gray-700 rotate-45 mx-auto -mt-1" />
                  </div>
               </div>
            
            </div>
            <ProgressBar
               value={capUsage * 100}
               color={capUsage > 0.95 ? 'bg-red-500' : capUsage > 0.80 ? 'bg-yellow-500' : 'bg-blue-500'}
               height="h-2"
            />
            {capUsage > 0.85 && (
               <p className="text-xs text-yellow-400 mt-2 flex items-center gap-1">
                  <TriangleAlert className="w-3.5 h-3.5 shrink-0" /> Approaching capacity — node will auto-invest in housing if res/capita ≥ 60.
               </p>
            )}
         </ModalSection>
         
         
         {/* ── Income sources + trade breakdown ── */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ModalSection className="flex flex-col" title="Income Sources" icon={<Coins className="w-4 h-4" />}>
               {Object.keys(node.income_sources || {}).length === 0 ? (
                  <p className="text-gray-600 text-xs">No fixed income sources defined.</p>
               ) : (
                  <div className="space-y-1.5 flex flex-col grow shrink-0">
                     {Object.entries(node.income_sources || {}).map(([source, amount]: [string, any]) => (
                        <StatRow
                           key={source}
                           label={source}
                           labelColor="text-gray-400 capitalize"
                           value={`+${Number(amount).toLocaleString()}`}
                           color="text-green-400"
                           size="md"
                        />
                     ))}
                     <div className="border-t border-gray-800 pt-1.5 !mt-auto !mb-0">
                        <StatRow
                           label="Total fixed"
                           labelColor="text-gray-300"
                           value={`+${Object.values(node.income_sources || {})
                              .reduce((a: number, b: any) => a + Number(b || 0), 0)
                              .toLocaleString()}`}
                           color="text-green-300"
                           size="md"
                        />
                     </div>
                  </div>
               )}
            
            </ModalSection>
            
            <ModalSection className="flex flex-col" title="Trade Balance" icon={<Dot color="#c084fc" />}>
               <div className="space-y-1.5 flex flex-col grow shrink-0">
                  <StatRow
                     label="Resources sold"
                     value={`−${tradeBalance.resourcesSold.toFixed(1)} res`}
                     color="text-orange-300"
                     size="md"
                  />
                  <StatRow
                     label="Credits gained"
                     value={`+${tradeBalance.creditsGained.toFixed(0)} cr`}
                     color="text-green-300"
                     size="md"
                  />
                  <StatRow
                     label="Resources bought"
                     value={`+${tradeBalance.resourcesBought.toFixed(1)} res`}
                     color="text-blue-300"
                     size="md"
                  />
                  <StatRow
                     label="Credits spent"
                     value={`−${tradeBalance.creditsSpent.toFixed(0)} cr`}
                     color="text-red-300"
                     size="md"
                  />
                  <div className="border-t border-gray-800 pt-2 !mt-auto !mb-0">
                     <StatRow
                        label="Net credits"
                        labelColor="text-gray-300"
                        value={`${tradeNetVal >= 0 ? '+' : ''}${tradeNetVal.toFixed(0)} cr`}
                        color={tradeNetVal >= 0 ? 'text-green-300' : 'text-red-300'}
                        size="md"
                     />
                  </div>
               </div>
            </ModalSection>
         </div>
         
         {/* ── Resource Investments ── */}
         {(() => {
            const nodeInvestments = investments.filter(inv => inv.node_id === node.id);
            if (nodeInvestments.length === 0) return null;
            return (
               <ModalSection title="Resource Investments" icon={<TrendingUp className="w-4 h-4" />} titleExtra={
                  <span className="text-xs text-gray-500">{nodeInvestments.length} active</span>
               }>
                  <ItemList
                     items={nodeInvestments.map(inv => {
                        const rn = resourceNodes.find(r => r.id === inv.resource_id);
                        const maxSh = rn?.max_total_shares ?? 100;
                        const bonus = Math.round(1000 * Math.sqrt(inv.shares / Math.max(maxSh, 1)));
                        const allInvForRn = investments.filter(i => i.resource_id === inv.resource_id);
                        const totalSold = allInvForRn.reduce((s, i) => s + i.shares, 0);
                        const TYPE_ICONS: Record<string, React.ReactNode> = {
                           oil: <Fuel className="w-3.5 h-3.5" />,
                           minerals: <Pickaxe className="w-3.5 h-3.5" />,
                           forest: <Trees className="w-3.5 h-3.5" />,
                           water: <Droplet className="w-3.5 h-3.5" />,
                           gas: <Flame className="w-3.5 h-3.5" />,
                           generic: <span>◆</span>,
                        };
                        const typeIcon = rn ? (TYPE_ICONS[rn.type] ?? <span>◆</span>) : <span>◆</span>;
                        return {
                           id: inv.resource_id,
                           icon: typeIcon,
                           label: rn?.name ?? `Resource #${inv.resource_id}`,
                           sub: `${inv.shares} shares · ${((inv.shares / Math.max(maxSh, 1)) * 100).toFixed(0)}% ownership · ${totalSold}/${maxSh} sold`,
                           value: `+${100 + bonus} /tick`,
                           valueColor: 'text-emerald-400',
                           variant: 'card' as const,
                           onClick: () => {
                              setSelectedNodeId(null);
                              setSelectedResourceNodeId(inv.resource_id);
                           },
                        };
                     })}
                  />
               </ModalSection>
            );
         })()}

         {/* ── Active connections ── */}
         <ModalSection title="Connections" icon={<Link className="w-4 h-4" />} titleExtra={
            <span className="text-xs text-gray-500">
               {nodeConnections.length} total · {tradingConns.length} trading
            </span>
         }>
            {nodeConnections.length === 0 ? (
               <p className="text-gray-600 text-sm">No connections.</p>
            ) : (
               <ItemList
                  items={nodeConnections.map((conn: any) => {
                     const otherId = conn.node_a_id === node.id ? conn.node_b_id : conn.node_a_id;
                     const other = nodes.find(n => n.id === otherId);
                     const isTrading = (Number(conn.last_trade_volume) || 0) > 0;
                     const maint = (Number(conn.maintenance_cost) || 0) / 2;
                     return {
                        id: conn.id,
                        icon:
                           <span className={`w-2 h-2 rounded-full inline-block ${isTrading ? 'bg-purple-400 animate-pulse' : 'bg-blue-600'}`} />,
                        label: other?.name ?? `Node #${otherId}`,
                        badge: other?.income_status,
                        sub: `traffic ${Number(conn.traffic_volume).toLocaleString()} · maint −${maint.toFixed(0)}`,
                        value: isTrading
                           ? `${(Number(conn.last_trade_volume) || 0).toFixed(1)} res · +${(Number(conn.last_trade_value) || 0).toFixed(0)} cr`
                           : undefined,
                        valueColor: 'text-purple-300',
                        variant: 'card' as const,
                        onClick: () => {
                           setSelectedNodeId(null);
                           setSelectedConnectionId(conn.id);
                        },
                     };
                  })}
               />
            )}
         </ModalSection>
      </DetailModal>
   );
};

export default NodeDetailModal;
